import { useRef } from 'react';
import {
  Flex,
  Link,
  Text,
  Tooltip,
  Icon,
  IconButton,
  Box,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  LinkBox,
  LinkOverlay,
  Heading,
  useDisclosure,
  useDimensions,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useIsUserInRole } from '@transport-insights/uikit';
import {
  FiArrowRight, FiArrowLeft, FiGrid, FiList,
} from 'react-icons/fi';
import { REPORT_LIST } from '@src/modules/regulatory-reporting/shared/constants';
import MoreInfoDrawer from '@src/components/shared/MoreInfoDrawer';

export default function Pagination({ reportName, isLoading, downloadButton, moreInfo }) {
  const isDeveloper = useIsUserInRole('Developer');
  const { isOpen: isReportListOpen, onOpen: onOpenReportList, onClose: onCloseReportList } = useDisclosure();
  const ref = useRef();
  const dimension = useDimensions(ref, true);
  const enabledReportsList = REPORT_LIST.filter((x) => x.enabled || isDeveloper);
  const { name } = REPORT_LIST.find((x) => x.name === reportName);

  const prevIndex = enabledReportsList.findIndex((report) => report.name === reportName) - 1;
  const nextIndex = enabledReportsList.findIndex((report) => report.name === reportName) + 1;

  const nextReport = nextIndex !== enabledReportsList.length ? enabledReportsList[nextIndex] : enabledReportsList[0];
  const prevReport = prevIndex !== -1 ? enabledReportsList[prevIndex] : enabledReportsList[enabledReportsList.length - 1];

  const isScrolling = dimension?.borderBox.top === 80;

  return (
    <Box
      position="sticky"
      top="80px"
      pt={isScrolling ? 2 : 0}
      pb={4}
      px={4}
      mx="-16px"
      background="gray.50"
      zIndex="200"
      ref={ref}
      boxShadow={isScrolling ? '1px 7px 10px -9px rgba(0,0,0,0.15);' : 'none'}
    >
      <Flex align="center" justify="space-between" mb={4}>
        <Flex align="center" justify="space-between">
          <Tooltip label="Back to Dashboard">
            <IconButton
              as={RouterLink}
              to="/regulatory-reporting/reports"
              borderRadius="full"
              colorScheme="blue"
              variant="outline"
              icon={<FiGrid />}
            />
          </Tooltip>
          <Text fontSize="sm" fontWeight="bold" color="gray.400" ml={4}>Regulatory Reporting</Text>
        </Flex>
        <Flex align="center" justify="space-between">
          {enabledReportsList.length > 1 && (
          <>
            {prevReport
            && (
            <Tooltip label={prevReport.name}>
              <Link
                as={RouterLink}
                to={`/regulatory-reporting/reports/${prevReport.url}`}
                color="brand.orange.500"
                display="flex"
                align="center"
                borderRadius="sm"
                p={2}
                _hover={{
                  textDecoration: 'none',
                  color: 'white',
                  background: 'brand.orange.500',
                }}
              >
                <Icon as={FiArrowLeft} boxSize={8} />
              </Link>
            </Tooltip>
            )}
            {nextReport
            && (
            <Tooltip label={nextReport.name}>
              <Link
                as={RouterLink}
                to={`/regulatory-reporting/reports/${nextReport.url}`}
                color="brand.orange.500"
                display="flex"
                align="center"
                borderRadius="sm"
                p={2}
                _hover={{
                  textDecoration: 'none',
                  color: 'white',
                  background: 'brand.orange.500',
                }}
              >
                <Icon as={FiArrowRight} boxSize={8} />
              </Link>
            </Tooltip>
            )}
            <Tooltip label="Reports List">
              <IconButton
                onClick={onOpenReportList}
                icon={<FiList />}
                borderRadius="full"
                colorScheme="blue"
                variant="outline"
                ml={3}
              />
            </Tooltip>
          </>
          )}
          {downloadButton && downloadButton}
          <MoreInfoDrawer title="More Information" size="xl" isLoading={isLoading}>
            {moreInfo}
          </MoreInfoDrawer>
        </Flex>
      </Flex>
      <Heading as="h1">
        {name}
      </Heading>
      <Modal isOpen={isReportListOpen} onClose={onCloseReportList} size="xl">
        <ModalOverlay
          bg="blackAlpha.700"
          backdropFilter="blur(4px)"
        />
        <ModalContent>
          <ModalHeader>Regulatory Reports</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {enabledReportsList.map((report) => (
              <LinkBox
                as="article"
                p={3}
                borderRadius="md"
                key={report.url}
                border="2px solid"
                borderColor={report.name === reportName ? 'brand.orange.500' : 'gray.50'}
                backgroundColor={report.name === reportName ? 'brand.orange.500' : 'white'}
                mb={4}
                transition="all 200ms"
                _hover={{ borderColor: 'brand.orange.500' }}
              >
                <Heading size="sm">
                  <LinkOverlay
                    as={RouterLink}
                    to={`/regulatory-reporting/reports/${report.url}`}
                    onClick={onCloseReportList}
                    color={report.name === reportName ? 'white' : 'gray.800'}
                    fontWeight="normal"
                    textDecoration="none"
                  >
                    {report.name}
                  </LinkOverlay>
                </Heading>
              </LinkBox>
            ))}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
